import { defineRecipe } from '@chakra-ui/react';

// =============================================================================
// OMNI AppHeader Recipe Collection
// =============================================================================
// This file contains all styling recipes for AppHeader components following
// the OMNI design system standards from omni_standard_css.css
// =============================================================================

// AppHeader Container Recipe
// =============================================================================
// Main header container with OMNI styling and responsive design
export const omniAppHeaderContainerRecipe = defineRecipe({
  base: {
    bg: 'omni.bg.surface',
    borderBottom: '1px solid',
    borderColor: 'omni.border.subtle',
    color: 'omni.text.primary',
    boxShadow: 'omni.shadow.subtle',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    w: 'full',
    px: { base: 2, sm: 4, md: 6, lg: 8 },
    py: { base: 2, sm: 3, md: 4 },
    minH: { base: '56px', md: '64px', lg: '70px' },
    fontSize: { base: 'xs', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' },
    gap: { base: 1, sm: 2, md: 3 },
    transition: 'all 0.2s ease-in-out',
  },
  variants: {
    elevated: {
      true: {
        boxShadow: 'omni.shadow.medium',
        borderBottomWidth: '2px',
      },
    },
    compact: {
      true: {
        minH: { base: '48px', md: '56px' },
        py: { base: 1, sm: 2, md: 3 },
      },
    },
  },
});

// AppHeader Section Recipe
// =============================================================================
// Left and right section containers within the header
export const omniAppHeaderSectionRecipe = defineRecipe({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: { base: 1, sm: 2, md: 3 },
    flex: 'none',
  },
  variants: {
    position: {
      left: {
        justifyContent: 'flex-start',
        flex: '1 1 auto',
      },
      right: {
        justifyContent: 'flex-end',
        flex: 'none',
      },
      center: {
        justifyContent: 'center',
        flex: '1 1 auto',
      },
    },
    spacing: {
      tight: {
        gap: { base: 0.5, sm: 1, md: 1.5 },
      },
      normal: {
        gap: { base: 1, sm: 2, md: 3 },
      },
      loose: {
        gap: { base: 2, sm: 3, md: 4 },
      },
    },
  },
  defaultVariants: {
    spacing: 'normal',
  },
});

// Brand Section Recipe
// =============================================================================
// Container for logo and brand title
export const omniBrandContainerRecipe = defineRecipe({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: { base: 1, sm: 2, md: 3 },
    flex: 'none',
  },
  variants: {
    size: {
      sm: {
        gap: { base: 1, sm: 1.5 },
      },
      md: {
        gap: { base: 1, sm: 2, md: 3 },
      },
      lg: {
        gap: { base: 2, sm: 3, md: 4 },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// Brand Icon Button Recipe
// =============================================================================
// Home/logo icon button with OMNI styling
export const omniBrandIconButtonRecipe = defineRecipe({
  base: {
    bg: 'transparent',
    color: 'omni.text.primary',
    border: 'none',
    borderRadius: 'omni.radius.medium',
    p: { base: 1, sm: 1.5, md: 2 },
    minW: { base: '32px', sm: '36px', md: '40px' },
    h: { base: '32px', sm: '36px', md: '40px' },
    fontSize: { base: 'md', sm: 'lg', md: 'xl' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    _hover: {
      bg: 'omni.bg.hover.subtle',
      color: 'omni.text.primary',
      transform: 'scale(1.05)',
    },
    _focus: {
      bg: 'omni.bg.hover.subtle',
      boxShadow: '0 0 0 2px var(--omni-border-focus)',
      outline: 'none',
    },
    _active: {
      bg: 'omni.bg.active',
      transform: 'scale(0.98)',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        transform: 'none',
      },
    },
  },
  variants: {
    variant: {
      default: {},
      emphasized: {
        bg: 'omni.bg.primary',
        color: 'white',
        _hover: {
          bg: 'omni.bg.primary.hover',
        },
        _focus: {
          bg: 'omni.bg.primary.hover',
        },
      },
      subtle: {
        color: 'omni.text.secondary',
        _hover: {
          color: 'omni.text.primary',
        },
      },
      contrast: {
        // High contrast variant for AppHeader without background
        bg: 'rgba(255, 255, 255, 0.9)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.200',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'scale(1.05)',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.500',
          transform: 'scale(0.98)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          outline: '2px solid',
          outlineColor: 'brand.200',
          outlineOffset: '2px',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.6)',
          color: 'gray.400',
          borderColor: 'gray.100',
          boxShadow: 'none',
          _hover: {
            bg: 'rgba(255, 255, 255, 0.6)',
            transform: 'none',
          },
        },
      },
    },
    size: {
      sm: {
        minW: '28px',
        h: '28px',
        fontSize: 'sm',
        p: 1,
      },
      md: {
        minW: { base: '32px', sm: '36px', md: '40px' },
        h: { base: '32px', sm: '36px', md: '40px' },
        fontSize: { base: 'md', sm: 'lg', md: 'xl' },
      },
      lg: {
        minW: { base: '40px', sm: '44px', md: '48px' },
        h: { base: '40px', sm: '44px', md: '48px' },
        fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// Brand Heading Recipe
// =============================================================================
// Main brand title with OMNI typography
export const omniBrandHeadingRecipe = defineRecipe({
  base: {
    color: 'omni.text.primary',
    fontWeight: '600',
    lineHeight: '1.2',
    m: 0,
    userSelect: 'none',
    fontSize: { base: 'md', sm: 'lg', md: 'xl', lg: '2xl', xl: '3xl' },
  },
  variants: {
    variant: {
      primary: {
        color: 'omni.text.primary',
      },
      secondary: {
        color: 'omni.text.secondary',
      },
      accent: {
        color: 'omni.primary.500',
      },
      contrast: {
        // High contrast variant for headers without background
        color: 'gray.900',
        textShadow:
          '0 1px 2px rgba(255, 255, 255, 0.8), 0 0 4px rgba(255, 255, 255, 0.6)',
        fontWeight: '700',
        letterSpacing: '0.025em',
      },
    },
    size: {
      sm: {
        fontSize: { base: 'sm', sm: 'md', md: 'lg' },
      },
      md: {
        fontSize: { base: 'md', sm: 'lg', md: 'xl', lg: '2xl' },
      },
      lg: {
        fontSize: { base: 'lg', sm: 'xl', md: '2xl', lg: '3xl', xl: '4xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

// Navigation Container Recipe
// =============================================================================
// Container for navigation elements like CheatSheet and History
export const omniNavigationContainerRecipe = defineRecipe({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: { base: 1, sm: 2, md: 3 },
    flex: 'none',
  },
  variants: {
    spacing: {
      tight: {
        gap: { base: 0.5, sm: 1 },
      },
      normal: {
        gap: { base: 1, sm: 2, md: 3 },
      },
      loose: {
        gap: { base: 2, sm: 3, md: 4 },
      },
    },
  },
  defaultVariants: {
    spacing: 'normal',
  },
});

// Select Component Recipe
// =============================================================================
// Country/Agency selector styling with OMNI design
export const omniHeaderSelectRecipe = defineRecipe({
  base: {
    bg: 'omni.bg.surface',
    color: 'omni.text.primary',
    border: '1px solid',
    borderColor: 'omni.border.subtle',
    borderRadius: 'omni.radius.medium',
    px: 3,
    py: 2,
    fontSize: { base: 'xs', sm: 'sm', md: 'sm' },
    minW: { base: '120px', sm: '140px', md: '150px' },
    maxW: { base: '140px', sm: '160px', md: '180px' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    paddingRight: '32px',
    _hover: {
      borderColor: 'omni.border.hover',
      bg: 'omni.bg.hover.subtle',
    },
    _focus: {
      borderColor: 'omni.border.focus',
      boxShadow: '0 0 0 1px var(--omni-border-focus)',
      outline: 'none',
    },
    _disabled: {
      bg: 'omni.bg.disabled',
      color: 'omni.text.disabled',
      borderColor: 'omni.border.disabled',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  variants: {
    variant: {
      default: {},
      outline: {
        bg: 'transparent',
        _hover: {
          bg: 'omni.bg.hover.subtle',
        },
      },
      filled: {
        bg: 'omni.bg.muted',
        borderColor: 'transparent',
        _hover: {
          bg: 'omni.bg.hover.muted',
        },
      },
      contrast: {
        // High contrast variant for selects without background
        bg: 'rgba(255, 255, 255, 0.95)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.300',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          boxShadow: '0 0 0 2px rgba(0, 161, 210, 0.2)',
          outline: 'none',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.7)',
          color: 'gray.400',
          borderColor: 'gray.200',
        },
      },
    },
    size: {
      sm: {
        px: 2,
        py: 1,
        fontSize: 'xs',
        minW: '100px',
        maxW: '120px',
      },
      md: {
        px: 3,
        py: 2,
        fontSize: { base: 'xs', sm: 'sm' },
        minW: { base: '120px', sm: '140px', md: '150px' },
        maxW: { base: '140px', sm: '160px', md: '180px' },
      },
      lg: {
        px: 4,
        py: 3,
        fontSize: 'sm',
        minW: '160px',
        maxW: '200px',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// Control Button Recipe
// =============================================================================
// Authentication buttons (Login/Logout) with OMNI styling
export const omniControlButtonRecipe = defineRecipe({
  base: {
    fontWeight: '500',
    borderRadius: 'omni.radius.medium',
    px: { base: 3, sm: 4, md: 6 },
    py: { base: 1.5, sm: 2, md: 2.5 },
    fontSize: { base: 'xs', sm: 'sm', md: 'sm' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    border: '1px solid transparent',
    minW: { base: '60px', sm: '70px', md: '80px' },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  variants: {
    variant: {
      primary: {
        bg: 'omni.primary.500',
        color: 'white',
        _hover: {
          bg: 'omni.primary.600',
          transform: 'translateY(-1px)',
          boxShadow: 'omni.shadow.medium',
        },
        _focus: {
          bg: 'omni.primary.600',
          boxShadow: '0 0 0 2px var(--omni-primary-200)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.primary.700',
          transform: 'translateY(0)',
        },
      },
      secondary: {
        bg: 'omni.bg.surface',
        color: 'omni.text.primary',
        borderColor: 'omni.border.subtle',
        _hover: {
          bg: 'omni.bg.hover.subtle',
          borderColor: 'omni.border.hover',
          transform: 'translateY(-1px)',
          boxShadow: 'omni.shadow.subtle',
        },
        _focus: {
          borderColor: 'omni.border.focus',
          boxShadow: '0 0 0 1px var(--omni-border-focus)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.bg.active',
          transform: 'translateY(0)',
        },
      },
      danger: {
        bg: 'omni.danger.500',
        color: 'white',
        _hover: {
          bg: 'omni.danger.600',
          transform: 'translateY(-1px)',
          boxShadow: 'omni.shadow.medium',
        },
        _focus: {
          bg: 'omni.danger.600',
          boxShadow: '0 0 0 2px var(--omni-danger-200)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.danger.700',
          transform: 'translateY(0)',
        },
      },
      ghost: {
        bg: 'transparent',
        color: 'omni.text.primary',
        _hover: {
          bg: 'omni.bg.hover.subtle',
        },
        _focus: {
          bg: 'omni.bg.hover.subtle',
          boxShadow: '0 0 0 2px var(--omni-border-focus)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.bg.active',
        },
      },
      contrast: {
        // High contrast variant for control buttons without background
        bg: 'rgba(255, 255, 255, 0.95)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.300',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        fontWeight: '600',
        _hover: {
          bg: 'white',
          borderColor: 'brand.400',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-1px)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          boxShadow: '0 0 0 2px rgba(0, 161, 210, 0.2)',
          outline: 'none',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.600',
          transform: 'translateY(0)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.7)',
          color: 'gray.400',
          borderColor: 'gray.200',
          boxShadow: 'none',
        },
      },
    },
    size: {
      xs: {
        px: 2,
        py: 1,
        fontSize: '2xs',
        minW: '50px',
      },
      sm: {
        px: 3,
        py: 1.5,
        fontSize: 'xs',
        minW: { base: '60px', sm: '70px' },
      },
      md: {
        px: { base: 3, sm: 4, md: 6 },
        py: { base: 1.5, sm: 2, md: 2.5 },
        fontSize: { base: 'xs', sm: 'sm' },
        minW: { base: '60px', sm: '70px', md: '80px' },
      },
      lg: {
        px: { base: 4, sm: 6, md: 8 },
        py: { base: 2, sm: 3, md: 3.5 },
        fontSize: { base: 'sm', sm: 'md' },
        minW: { base: '80px', sm: '90px', md: '100px' },
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },
});

// Control Icon Button Recipe
// =============================================================================
// Icon buttons like theme toggle with OMNI styling
export const omniControlIconButtonRecipe = defineRecipe({
  base: {
    bg: 'transparent',
    color: 'omni.text.primary',
    border: 'none',
    borderRadius: 'omni.radius.medium',
    p: { base: 1.5, sm: 2, md: 2.5 },
    minW: { base: '36px', sm: '40px', md: '44px' },
    h: { base: '36px', sm: '40px', md: '44px' },
    fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    _hover: {
      bg: 'omni.bg.hover.subtle',
      color: 'omni.text.primary',
      transform: 'scale(1.05)',
    },
    _focus: {
      bg: 'omni.bg.hover.subtle',
      boxShadow: '0 0 0 2px var(--omni-border-focus)',
      outline: 'none',
    },
    _active: {
      bg: 'omni.bg.active',
      transform: 'scale(0.98)',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        transform: 'none',
      },
    },
  },
  variants: {
    variant: {
      default: {},
      emphasized: {
        bg: 'omni.bg.muted',
        _hover: {
          bg: 'omni.bg.hover.muted',
        },
      },
      subtle: {
        color: 'omni.text.secondary',
        _hover: {
          color: 'omni.text.primary',
          bg: 'omni.bg.hover.subtle',
        },
      },
      contrast: {
        // High contrast variant for icon buttons without background
        bg: 'rgba(255, 255, 255, 0.9)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.200',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'scale(1.05)',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.500',
          transform: 'scale(0.98)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          outline: '2px solid',
          outlineColor: 'brand.200',
          outlineOffset: '2px',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.6)',
          color: 'gray.400',
          borderColor: 'gray.100',
          boxShadow: 'none',
          _hover: {
            bg: 'rgba(255, 255, 255, 0.6)',
            transform: 'none',
          },
        },
      },
    },
    size: {
      sm: {
        minW: '32px',
        h: '32px',
        fontSize: 'md',
        p: 1.5,
      },
      md: {
        minW: { base: '36px', sm: '40px', md: '44px' },
        h: { base: '36px', sm: '40px', md: '44px' },
        fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
      },
      lg: {
        minW: { base: '44px', sm: '48px', md: '52px' },
        h: { base: '44px', sm: '48px', md: '52px' },
        fontSize: { base: 'xl', sm: '2xl', md: '3xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

// =============================================================================
// EXPORTS
// =============================================================================
export const omniAppHeaderRecipes = {
  omniAppHeaderContainer: omniAppHeaderContainerRecipe,
  omniAppHeaderSection: omniAppHeaderSectionRecipe,
  omniBrandContainer: omniBrandContainerRecipe,
  omniBrandIconButton: omniBrandIconButtonRecipe,
  omniBrandHeading: omniBrandHeadingRecipe,
  omniNavigationContainer: omniNavigationContainerRecipe,
  omniHeaderSelect: omniHeaderSelectRecipe,
  omniControlButton: omniControlButtonRecipe,
  omniControlIconButton: omniControlIconButtonRecipe,
};
